import { useMediaQuery } from '@abyss/web/hooks/useMediaQuery';
import { config } from '@abyss/web/tools/config';
import { Button } from '@abyss/web/ui/Button';
import { Divider } from '@abyss/web/ui/Divider';
import { Flex } from '@abyss/web/ui/Flex';
import { IconSymbol } from '@abyss/web/ui/IconSymbol';
import { find } from 'lodash';
import React, { useContext } from 'react';

import { isUmrMember } from '../../../common/Utils/memberUtils/memberUtils';
import { PortalContext } from '../../../context/PortalContext';
import { useLagoon } from '../../../hooks/useLagoon';
import { StoreKeys } from '../../../hooks/useStore/state';
import { useStore } from '../../../hooks/useStore/useStore';
import { buildFaqUrl } from '../../../utils/smartChoice.utils';
import { adobeLinkTrackEvent } from '../../AdobeTagging/adobeLinkTrackEvent';
import { ConstantsLagoon } from '../../ConstantsLagoon';
import { phoneOnly } from '../../ConstantsStyles';
import { ContentWrapper } from '../../ContentWrapper';
import { FAQLink } from '../../SmartChoiceBreakDownDrawer/SmartChoiceBreakDown.styles';
import { replaceSpaceToUpperCase } from '../../Utils/replaceString';
import {
  ImageHeaderContainer,
  OnboardingDisclaimerContainer,
  OnboardingHeading,
  StyledFAQContainer,
} from './OnboardingForm.styled';
import {
  DescriptionTextOnboarding,
  FlexContainer,
  HeadingTextOnBoarding,
  ListItemContentContainer,
  ListItemIconContainer,
  OnboardingDisclaimerText,
  StyledDrawer,
  StyledMarkdown,
} from './SmartChoice.styled';

export const properties: any = {
  buttonStyles: {
    backgroundColor: '$primary2',
    border: '1px solid $gray4',
    padding: '$sm $lg $sm $md',
    height: '2.625em',
    fontWeight: '$semibold',
    borderRadius: '24px',
    color: '$gray8',
  },
  drawerSize: 492,
  drawerposition: 'right',
};

export interface Props {
  isOpen: boolean;
  handleCloseDrawer: () => void;
  handleOpenSetPreferencesModal: () => void;
}

export const ONBOARDING_SMART_CHOICE = `${config(
  'CDN_BASE_URL'
)}/cdn/assets/images/smart-choice-onboarding.png`;

export const OnboardingForm = ({
  isOpen,
  handleCloseDrawer,
  handleOpenSetPreferencesModal,
}: Props) => {
  const { portalData } = useContext(PortalContext);
  const mobileScreen = useMediaQuery(phoneOnly);

  const lagoonData = useLagoon('ui-messaging')();
  const isWidget = useStore(StoreKeys?.IS_WIDGET);
  const SC_ONBOARDING_HEADING_DESC = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.SMART_CHOICE.SC_ONBOARDING_HEADING_DESC,
  });
  const SC_ONBOARDING_QUALITY_CARE = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.SMART_CHOICE.SC_ONBOARDING_QUALITY_CARE,
  });
  const SC_ONBOARDING_BENEFITS = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.SMART_CHOICE.SC_ONBOARDING_BENEFITS,
  });
  const SC_ONBOARDING_CONVENIENCE = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.SMART_CHOICE.SC_ONBOARDING_CONVENIENCE,
  });
  const SC_ONBOARDING_PERSONAL_FIT = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.SMART_CHOICE.SC_ONBOARDING_PERSONAL_FIT,
  });
  const SC_ONBOARDING_SET_PREFERENCES = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.SMART_CHOICE
      .SC_ONBOARDING_SET_PREFERENCES,
  });
  const SC_ONBOARDING_FAQ_LINK = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.SMART_CHOICE.SC_ONBOARDING_FAQ_LINK,
  });
  const SC_ONBOARDING_DISCLAIMER_TEXT = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.SMART_CHOICE
      .SC_ONBOARDING_DISCLAIMER_TEXT,
  });
  const isUmr = isUmrMember(portalData?.portalName);
  const baseUrl = isUmr ? config('UMR_BASE_URL') : config('BASE_URL');

  const ListItems = ({ heading, description, divider }) => (
    <>
      <FlexContainer>
        <ListItemIconContainer>
          <IconSymbol color="$success1" icon="check" size="24px" />
        </ListItemIconContainer>
        <ListItemContentContainer>
          <HeadingTextOnBoarding
            data-auto-testid={`heading-${replaceSpaceToUpperCase(heading)}`}
            data-testid={`heading-${replaceSpaceToUpperCase(heading)}`}
          >
            {heading}
          </HeadingTextOnBoarding>
          <DescriptionTextOnboarding
            data-auto-testid={`description-${replaceSpaceToUpperCase(heading)}`}
            data-testid={`description-${replaceSpaceToUpperCase(heading)}`}
          >
            {description}
          </DescriptionTextOnboarding>
        </ListItemContentContainer>
      </FlexContainer>
      {divider && <Divider />}
    </>
  );

  const onClickSetPreferences = () => {
    handleCloseDrawer();
    adobeLinkTrackEvent({
      name: 'Set preferences',
      location: 'modal:A better way to find providers',
    });
    handleOpenSetPreferencesModal();
  };

  return (
    <React.Fragment>
      <StyledDrawer
        data-auto-testid="onboarding-smart-choice-drawer"
        data-testid="onboarding-smart-choice-drawer"
        isOpen={isOpen}
        onClose={() => {
          handleCloseDrawer();
          adobeLinkTrackEvent({
            name: 'close',
            location: 'modal:A better way to find providers',
          });
        }}
        position={properties.position}
        size={properties.drawerSize}
        title={
          <React.Fragment>
            <ImageHeaderContainer>
              <img
                alt=""
                src={ONBOARDING_SMART_CHOICE}
                width={`${mobileScreen ? 138 : 172}`}
              />
            </ImageHeaderContainer>
            <OnboardingHeading data-testid="onboarding-smart-choice-heading">
              {SC_ONBOARDING_HEADING_DESC?.title}
            </OnboardingHeading>
          </React.Fragment>
        }
      >
        <ContentWrapper style={{ padding: mobileScreen ? '0px' : '24px' }}>
          <Flex data-testid="smart-choice-sub-heading">
            <StyledMarkdown>
              {SC_ONBOARDING_HEADING_DESC?.message}
            </StyledMarkdown>
          </Flex>
          <ListItems
            description={SC_ONBOARDING_QUALITY_CARE?.message}
            divider
            heading={SC_ONBOARDING_QUALITY_CARE?.title}
          />
          <ListItems
            description={SC_ONBOARDING_BENEFITS?.message}
            divider
            heading={SC_ONBOARDING_BENEFITS?.title}
          />
          <ListItems
            description={SC_ONBOARDING_CONVENIENCE?.message}
            divider
            heading={SC_ONBOARDING_CONVENIENCE?.title}
          />
          <ListItems
            description={SC_ONBOARDING_PERSONAL_FIT?.message}
            divider={false}
            heading={SC_ONBOARDING_PERSONAL_FIT?.title}
          />
          <OnboardingDisclaimerContainer>
            <OnboardingDisclaimerText>
              {SC_ONBOARDING_DISCLAIMER_TEXT?.message}
            </OnboardingDisclaimerText>
          </OnboardingDisclaimerContainer>
          <Button
            css={{ width: '100%', margin: '32px 0px' }}
            data-testid="set-preferences-button"
            onClick={onClickSetPreferences}
          >
            {SC_ONBOARDING_SET_PREFERENCES?.title}
          </Button>
          <StyledFAQContainer>
            <FAQLink
              data-auto-testid="faq-link"
              data-testid="faq-link"
              href={buildFaqUrl(baseUrl, isWidget)}
              onClick={() => {
                adobeLinkTrackEvent({
                  name: 'smart choice faqs',
                  location: 'modal:A better way to find providers',
                });
              }}
            >
              {SC_ONBOARDING_FAQ_LINK?.message}
            </FAQLink>
          </StyledFAQContainer>
        </ContentWrapper>
      </StyledDrawer>
    </React.Fragment>
  );
};
